/**
 * PATH: packages/job-hub/src/jobs/djangoJobHubClient.ts
 * FILE_ID: JOBHUB-JOBS-DJANGO-CLIENT-001
 * PURPOSE: A reusable JobHubHttpClient that maps the shared <JobHubPage>'s
 *          tenant-agnostic calls onto the STANDARD Django platform API
 *          (report/ app, /api/report/...). Any tenant on the standard
 *          backend (bravada first) wraps its Django axios with this and
 *          injects it via <JobHubDataAdapterProvider> — no page fork.
 * WHY:     Job Standard View parity arc, Phase 4. S4 rank 12: the route
 *          table now lives in ./client/* segment modules; THE SEGMENT ORDER
 *          BELOW IS LOAD-BEARING (collections before schedule before job
 *          catch-alls — route-order traps). Public exports unchanged.
 * FLOW:
 * ┌────────────────────────┐  ┌──────────────────────┐  ┌────────────────┐
 * │ hub call (/jobs/:id …)  │→ │ resolveDjangoRoute    │→ │ Django call +   │
 * │ via JobHubHttpClient    │  │ (ordered ./client/*)  │  │ reshaped result │
 * └────────────────────────┘  └──────────────────────┘  └────────────────┘
 * DEPENDENCIES:
 * - ./jobHubDataAdapter: JobHubHttpClient / JobHubRequestConfig / response types.
 * - ./client/*: ordered route-table segments + pure file reshapers.
 * RELATED:
 * - apps/bravada (wires this into the job route).
 * - report/views/job_*_hub.py (the Django endpoints this targets).
 */
import type {
  JobHubHttpClient,
  JobHubRequestConfig,
  JobHubHttpResponse,
} from './jobHubDataAdapter'
import type { Verb, DjangoRoute, RouteContext, SegmentResolver } from './client/routeTypes'
import { resolveCollectionRoutes } from './client/resolveCollectionRoutes'
import { resolveMoneyRoutes } from './client/resolveMoneyRoutes'
import { resolveScheduleRoutes } from './client/resolveScheduleRoutes'
import { resolveJobRoutes } from './client/resolveJobRoutes'

// Re-exported for existing consumers/tests — moved verbatim to ./client/.
export { mapDjangoFile, reshapeFilesPayload } from './client/filesReshape'
export type { DjangoRoute } from './client/routeTypes'

/** Ordered route table. Segment order preserves the pre-split check order. */
const SEGMENTS: readonly SegmentResolver[] = [
  // Money-ledger routes first: /jobs/:id/money-documents must beat the
  // bare /jobs/:id catch-all in resolveJobRoutes (route-order trap).
  resolveMoneyRoutes,
  resolveCollectionRoutes,
  resolveScheduleRoutes,
  resolveJobRoutes,
]

/**
 * resolveDjangoRoute
 * ------------------
 * Pure mapping from a hub (verb, url) to a Django route descriptor. The
 * order of checks matters: more specific paths (…/files/upload, …/stages)
 * must precede the bare …/:id catch-alls — segments run sequentially and
 * each preserves its internal ordering verbatim.
 */
export function resolveDjangoRoute(
  verb: Verb,
  rawUrl: string,
  orgId?: number,
  requestParams?: Record<string, unknown>,
): DjangoRoute {
  const path = rawUrl.split('?')[0]
  const match = (re: string) => path.match(new RegExp(`^${re}/?$`))
  // The platform job-schedule endpoints resolve the org from ``?tenant=``
  // (resolve_organisation does NOT read the org header the other report
  // endpoints use), so schedule/people routes must carry it explicitly.
  const tenantParam = orgId != null ? { tenant: orgId } : undefined

  // Absolute URLs / explicit download links pass straight through.
  if (/^https?:\/\//i.test(rawUrl) || match(`/job-files/([^/?]+)/download`)) {
    return { kind: 'http', method: verb, url: rawUrl }
  }

  const ctx: RouteContext = { verb, rawUrl, path, match, tenantParam, requestParams }
  for (const segment of SEGMENTS) {
    const route = segment(ctx)
    if (route) return route
  }

  // Unknown route: forward verbatim so a missing mapping is loud, not silent.
  return { kind: 'http', method: verb, url: rawUrl }
}

function mergeParams(
  config: JobHubRequestConfig | undefined,
  extra: Record<string, unknown> | undefined,
): JobHubRequestConfig | undefined {
  if (!extra) return config
  return { ...(config ?? {}), params: { ...(config?.params ?? {}), ...extra } }
}

/**
 * createDjangoJobHubClient
 * ------------------------
 * Wrap a Django axios-shaped client into a JobHubHttpClient. Every hub
 * verb routes through ``resolveDjangoRoute`` then forwards to ``base``
 * (or answers synthetically). Reshaping is applied to the response data
 * so the hub sees the exact shapes it expects.
 *
 * ``opts.orgId`` is required only for the Schedule tab — the platform
 * job-schedule endpoints scope by ``?tenant=``. Tenants that gate the schedule
 * tab off (e.g. Bravada) can omit it.
 */
export function createDjangoJobHubClient(
  base: JobHubHttpClient,
  opts: { orgId?: number } = {},
): JobHubHttpClient {
  async function run<T>(
    verb: Verb,
    url: string,
    data: unknown,
    config: JobHubRequestConfig | undefined,
  ): Promise<JobHubHttpResponse<T>> {
    const route = resolveDjangoRoute(verb, url, opts.orgId, config?.params)

    if (route.kind === 'synthetic') {
      return { data: route.synthetic as T }
    }
    if (route.kind === 'reject') {
      return Promise.reject(new Error(route.rejectReason ?? 'Unsupported on the standard platform.'))
    }

    const method = route.method ?? verb
    const target = route.url ?? url
    const cfg = mergeParams(config, route.params)

    let resp: JobHubHttpResponse<T>
    if (method === 'get') {
      resp = await base.get<T>(target, cfg)
    } else if (method === 'delete') {
      resp = await base.delete<T>(target, cfg)
    } else if (method === 'post') {
      resp = await base.post<T>(target, data, cfg)
    } else if (method === 'put') {
      resp = await base.put<T>(target, data, cfg)
    } else {
      resp = await base.patch<T>(target, data, cfg)
    }

    if (route.reshape) {
      return { ...resp, data: route.reshape(resp.data) as T }
    }
    return resp
  }

  return {
    get: (url, config) => run('get', url, undefined, config),
    post: (url, data, config) => run('post', url, data, config),
    put: (url, data, config) => run('put', url, data, config),
    patch: (url, data, config) => run('patch', url, data, config),
    delete: (url, config) => run('delete', url, undefined, config),
  }
}
